/**
 * Clerk magic-code sign-in wrapper.
 *
 * Two modes:
 *   - Production: Clerk SDK (real magic-code emails, real JWTs).
 *   - Dev fallback: when CLERK_PUBLISHABLE_KEY is empty (no Clerk app set
 *     up locally), this module short-circuits to a stub flow that yields
 *     a `dev:<sub>:<email>` token the server's app/services/auth.py also
 *     accepts in dev mode.
 *
 * All UI lives in src/taskpane/signIn.ts. This module is pure state +
 * Clerk calls, no DOM access.
 */
import { Clerk } from "@clerk/clerk-js";

export type AuthState =
  | { kind: "loading" }
  | { kind: "signed_out" }
  | { kind: "code_sent"; email: string }
  | { kind: "signed_in"; email: string; sub: string };

const CLERK_PUBLISHABLE_KEY = process.env.CLERK_PUBLISHABLE_KEY;
const DEV_MODE = !CLERK_PUBLISHABLE_KEY;
const DEV_TOKEN_KEY = "cogseven.companion.devToken";

/**
 * Cross-iframe token cache. Office add-in dialogs (PDF import, etc.) run
 * in a separate window from the task pane and can't access the Clerk
 * instance directly. localStorage is shared across same-origin iframes,
 * so the task pane writes the latest JWT here whenever it fetches one,
 * and dialogs read it on demand.
 *
 * Tokens are short-lived (Clerk default: 1 minute); the task pane
 * refreshes the cache on every outbound call, and any code path that
 * opens a dialog refreshes the cache immediately beforehand.
 */
const PROD_TOKEN_CACHE_KEY = "cogseven.companion.prodTokenCache";

let clerk: Clerk | null = null;
let clerkLoaded = false;
let state: AuthState = { kind: "loading" };
const listeners = new Set<(s: AuthState) => void>();

/**
 * Tracks whether the current code-entry round is verifying a sign-IN
 * (existing Clerk user) or a sign-UP (auto-created on first contact).
 * Set by requestMagicCode, read by verifyMagicCode. Reset on sign-out.
 */
let signUpFlow = false;

/** True if `err` is a Clerk API error whose .errors[] contains the given code. */
function isClerkErrorWithCode(err: unknown, code: string): boolean {
  if (typeof err !== "object" || err === null) return false;
  const errors = (err as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return false;
  return errors.some(
    (e) =>
      typeof e === "object" &&
      e !== null &&
      (e as { code?: string }).code === code,
  );
}

function emit(): void {
  for (const fn of listeners) fn(state);
}

function setState(next: AuthState): void {
  state = next;
  emit();
}

export function getAuthState(): AuthState {
  return state;
}

export function onAuthStateChange(fn: (s: AuthState) => void): () => void {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

/**
 * Bootstrap. Resolves once we know if the user is signed in already (real
 * Clerk session or dev token in localStorage). Call once at startup.
 */
export async function initAuth(): Promise<void> {
  if (DEV_MODE) {
    const cached = localStorage.getItem(DEV_TOKEN_KEY);
    if (cached) {
      const parts = cached.split(":");
      if (parts.length === 3) {
        setState({ kind: "signed_in", email: parts[2], sub: parts[1] });
        return;
      }
    }
    setState({ kind: "signed_out" });
    return;
  }

  clerk = new Clerk(CLERK_PUBLISHABLE_KEY);
  await clerk.load();
  clerkLoaded = true;

  if (clerk.session) {
    const email = clerk.user?.primaryEmailAddress?.emailAddress ?? "";
    const sub = clerk.user?.id ?? "";
    setState({ kind: "signed_in", email, sub });
  } else {
    setState({ kind: "signed_out" });
  }
}

/**
 * Request a magic code be sent to the given email. Tries sign-in first;
 * if Clerk says the account doesn't exist, automatically pivots to sign-up
 * so first-time testers don't need to be pre-provisioned in the dashboard.
 */
export async function requestMagicCode(email: string): Promise<void> {
  signUpFlow = false;

  if (DEV_MODE) {
    setState({ kind: "code_sent", email });
    return;
  }
  if (!clerk || !clerkLoaded) throw new Error("Clerk not initialized");

  try {
    await clerk.client!.signIn.create({
      identifier: email,
      strategy: "email_code",
    });
  } catch (err: unknown) {
    if (!isClerkErrorWithCode(err, "form_identifier_not_found")) {
      throw err;
    }
    // No account yet — create one, then ask Clerk to email the code.
    signUpFlow = true;
    await clerk.client!.signUp.create({ emailAddress: email });
    await clerk.client!.signUp.prepareEmailAddressVerification({
      strategy: "email_code",
    });
  }
  setState({ kind: "code_sent", email });
}

/**
 * Submit the magic code the user typed. On success, transitions to
 * signed_in and a JWT is available via getToken().
 */
export async function verifyMagicCode(code: string): Promise<void> {
  if (state.kind !== "code_sent") {
    throw new Error("verifyMagicCode called in wrong state");
  }
  const email = state.email;

  if (DEV_MODE) {
    // Dev: synthesize a stable user id from the email so repeat sign-ins
    // resolve to the same appUser row.
    const sub = "dev_" + hashEmail(email);
    const token = `dev:${sub}:${email}`;
    localStorage.setItem(DEV_TOKEN_KEY, token);
    setState({ kind: "signed_in", email, sub });
    return;
  }
  if (!clerk || !clerkLoaded) throw new Error("Clerk not initialized");

  let createdSessionId: string | null | undefined;
  if (signUpFlow) {
    const result = await clerk.client!.signUp.attemptEmailAddressVerification({ code });
    if (result.status !== "complete") {
      throw new Error(`sign-up incomplete (status=${result.status})`);
    }
    createdSessionId = result.createdSessionId;
  } else {
    const result = await clerk.client!.signIn.attemptFirstFactor({
      strategy: "email_code",
      code,
    });
    if (result.status !== "complete") {
      throw new Error(`sign-in incomplete (status=${result.status})`);
    }
    createdSessionId = result.createdSessionId;
  }

  // Activate the new session, then clear the flow flag.
  if (createdSessionId) {
    await clerk.setActive({ session: createdSessionId });
  }
  signUpFlow = false;
  const userEmail = clerk.user?.primaryEmailAddress?.emailAddress ?? email;
  const sub = clerk.user?.id ?? "";
  setState({ kind: "signed_in", email: userEmail, sub });
}

/**
 * Fetch a fresh JWT to send to the API. Returns null if signed out.
 *
 * Two contexts:
 *   - Task pane: has the Clerk instance; gets a fresh token from
 *     `clerk.session.getToken()` and writes it to localStorage so any
 *     same-origin dialog window can read it.
 *   - Dialog: doesn't have a Clerk instance; reads the most recent
 *     token from localStorage (set by the task pane).
 */
export async function getToken(): Promise<string | null> {
  if (DEV_MODE) {
    return localStorage.getItem(DEV_TOKEN_KEY);
  }

  // Task-pane path: refresh from Clerk and update the cache.
  if (clerk && clerkLoaded && clerk.session) {
    const token = await clerk.session.getToken();
    if (token) {
      localStorage.setItem(PROD_TOKEN_CACHE_KEY, token);
    } else {
      localStorage.removeItem(PROD_TOKEN_CACHE_KEY);
    }
    return token;
  }

  // Dialog / iframe path: read whatever the task pane last cached.
  // May be stale if the dialog was open longer than the token lifetime;
  // in that case the API will 401 and the user can re-trigger the action
  // (the task pane will refresh and re-cache).
  return localStorage.getItem(PROD_TOKEN_CACHE_KEY);
}

export async function signOut(): Promise<void> {
  signUpFlow = false;
  if (DEV_MODE) {
    localStorage.removeItem(DEV_TOKEN_KEY);
    setState({ kind: "signed_out" });
    return;
  }
  // Clear the cross-iframe token cache so a dialog opened after sign-out
  // can't reuse a stale JWT.
  localStorage.removeItem(PROD_TOKEN_CACHE_KEY);
  if (!clerk) return;
  await clerk.signOut();
  setState({ kind: "signed_out" });
}

export function isDevMode(): boolean {
  return DEV_MODE;
}

function hashEmail(email: string): string {
  // Tiny non-crypto stable hash for the dev-mode sub.
  let h = 5381;
  for (let i = 0; i < email.length; i++) {
    h = ((h << 5) + h + email.charCodeAt(i)) >>> 0;
  }
  return h.toString(16);
}
